import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { getMessage } from "../../../../data/locales";
import CourseTestPreviewModal from "../../../../components/CourseTestPreviewModal";
import CourseTestTemarioLinker from "../../../../components/CourseTestTemarioLinker";
import { cloneTestForEditor } from "./cloneTestForEditor";
import {
  filterNonBlankTestPreguntas,
  validateTestPreguntasForSave,
} from "./validateTestPreguntasForSave";
import { confirmDeleteTestQuestion } from "../../../../utils/confirmDeleteTestQuestion";
import { uploadTestQuestionImage } from "../../../../utils/uploadTestQuestionImage";
import { removeTestRefsByTituloFromContenido } from "../../../../utils/removeTestRefsByTitulo";
import { getNamedCourseTabs, hasUnnamedCourseTabs } from "../../../../utils/normalizeCourseShape";
import { scrollElementIntoView } from "../../../../utils/scrollElementIntoView";
import ViewportSelect from "../../../../components/ViewportSelect";
import {
  formatTestQuestionSidebarPreview,
  formatTestQuestionSidebarTitle,
} from "../../../../utils/formatTestQuestionSidebarLabel";
import { moveCourseTestToIndex, resolveEditingIndexAfterMove } from "../../../../utils/courseTestsReorder";

const preguntaShape = PropTypes.shape({
  pregunta: PropTypes.string,
  imagenUrl: PropTypes.string,
  respuestas: PropTypes.arrayOf(PropTypes.string),
  correcta: PropTypes.number,
  explicacion: PropTypes.string,
});

const testShape = PropTypes.shape({
  titulo: PropTypes.string,
  tipo: PropTypes.string,
  tiempo: PropTypes.number,
  preguntasPorIntento: PropTypes.number,
  pestanas: PropTypes.arrayOf(PropTypes.string),
  preguntas: PropTypes.arrayOf(preguntaShape),
});

const testsPorBloqueShape = PropTypes.shape({
  porBloque: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)).isRequired,
  bloquesOrdenados: PropTypes.arrayOf(PropTypes.string).isRequired,
});

const TITLE_TESTS = "Requiere permiso para gestionar tests";

function createEmptyQuestion() {
  return { pregunta: "", respuestas: ["", "", "", ""], correcta: null, explicacion: "" };
}

function createEmptyTest() {
  return {
    titulo: "",
    tipo: "temario",
    tiempo: 0,
    preguntas: [createEmptyQuestion()],
    pestanas: [],
  };
}

function titleWhenNeedBlocksFirst(pestanasCount, canManageTests) {
  if (pestanasCount === 0) {
    return "Añade bloques en Datos principales";
  }
  if (canManageTests) {
    return undefined;
  }
  return "Requiere permiso";
}

function PreviewModalSlot({ previewTest, onClose }) {
  if (!previewTest) {
    return null;
  }
  return (
    <CourseTestPreviewModal
      test={previewTest}
      title="Vista previa del test"
      onClose={onClose}
    />
  );
}

PreviewModalSlot.propTypes = {
  previewTest: testShape,
  onClose: PropTypes.func.isRequired,
};

function DeleteTestConfirmModal({ open, onConfirm, onCancel }) {
  if (!open) {
    return null;
  }
  return (
    <div className="modal-confirmacion">
      <div className="modal-contenido">
        <p>¿Estás seguro de que deseas eliminar este test?</p>
        <div className="modal-botones">
          <button type="button" onClick={onConfirm}>
            Sí, eliminar
          </button>
          <button type="button" onClick={onCancel}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

DeleteTestConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

function deleteCourseTestAtIndex({
  tests,
  testIndex,
  indexEdicion,
  setTests,
  setBloquesContenido,
  setModoEdicion,
  setIndexEdicion,
  resetTestEditor,
}) {
  const testToDelete = tests[testIndex];
  const deletedTitulo = (testToDelete?.titulo ?? "").trim();
  if (deletedTitulo) {
    setBloquesContenido((prev) =>
      removeTestRefsByTituloFromContenido(prev, deletedTitulo),
    );
  }
  const nextTests = tests.filter((_, currentIndex) => currentIndex !== testIndex);
  setTests(nextTests);
  if (indexEdicion === undefined) {
    return;
  }
  if (testIndex === indexEdicion) {
    setModoEdicion(false);
    setIndexEdicion(undefined);
    resetTestEditor();
    return;
  }
  if (testIndex < indexEdicion) {
    setIndexEdicion(indexEdicion - 1);
  }
}

export default function EditarCursoTestsTab({
  courseId,
  uploadStatus,
  pestanasCurso,
  canManageTests,
  testActual,
  setTestActual,
  preguntas,
  setPreguntas,
  preguntaSeleccionada,
  setPreguntaSeleccionada,
  preguntaActual,
  modoEdicion,
  setModoEdicion,
  indexEdicion,
  setIndexEdicion,
  tests,
  setTests,
  setMensaje,
  testsPorBloque,
  bloquesContenido,
  setBloquesContenido,
}) {
  const navigate = useNavigate();
  const namedPestanas = getNamedCourseTabs(pestanasCurso);
  const unnamedTabsPresent = hasUnnamedCourseTabs(pestanasCurso);
  const uploadBusy = uploadStatus === "reading" || uploadStatus === "uploading";
  const testsLocked = canManageTests === false;
  const blockSelectTitle = titleWhenNeedBlocksFirst(namedPestanas.length, canManageTests);
  const saveTestTitle = titleWhenNeedBlocksFirst(namedPestanas.length, canManageTests);
  const primaryBtnNoBlockOrLocked = namedPestanas.length === 0 || testsLocked;
  const editorDisabled = testsLocked || uploadBusy;
  const testEditorRef = useRef(null);
  const questionEditorRef = useRef(null);
  const [previewTest, setPreviewTest] = useState(null);
  const [deleteTestIndex, setDeleteTestIndex] = useState(undefined);

  const scrollToTestEditor = () => {
    requestAnimationFrame(() => {
      scrollElementIntoView(testEditorRef.current, { behavior: "smooth", block: "start" });
    });
  };

  const scrollToQuestionEditor = () => {
    requestAnimationFrame(() => {
      scrollElementIntoView(questionEditorRef.current, { behavior: "smooth", block: "center" });
    });
  };

  const resetTestEditor = () => {
    const emptyTest = createEmptyTest();
    setTestActual(emptyTest);
    setPreguntas(emptyTest.preguntas);
    setPreguntaSeleccionada(0);
  };

  const cancelTestEdition = () => {
    resetTestEditor();
    setModoEdicion(false);
    setIndexEdicion(undefined);
    setMensaje("✏️ Edición de test cancelada.");
  };

  const deleteTestAtIndex = (testIndex) => {
    deleteCourseTestAtIndex({
      tests,
      testIndex,
      indexEdicion,
      setTests,
      setBloquesContenido,
      setModoEdicion,
      setIndexEdicion,
      resetTestEditor,
    });
  };

  const confirmDeleteSelectedTest = () => {
    deleteTestAtIndex(deleteTestIndex);
    setDeleteTestIndex(undefined);
    setMensaje("🗑️ Test eliminado (pendiente de guardar en el servidor).");
  };

  const moveTestToIndex = (testIndex, targetIndex) => {
    const nextTests = moveCourseTestToIndex(tests, testIndex, targetIndex);
    if (!nextTests) {
      return;
    }
    setTests(nextTests);
    setIndexEdicion((currentIndex) => resolveEditingIndexAfterMove(currentIndex, testIndex, targetIndex));
    setMensaje("↕️ Orden de tests actualizado (pendiente de guardar en el servidor).");
  };

  useEffect(() => {
    if (!modoEdicion) {
      return;
    }
    scrollToTestEditor();
  }, [modoEdicion, indexEdicion]);

  const appendTestToCourse = () => {
    if (testActual.titulo.trim() === "") {
      setMensaje("⚠️ Añade título al test.");
      return;
    }
    if (!testActual.pestanas?.[0]) {
      setMensaje("⚠️ Selecciona un bloque (pestaña) para este test.");
      return;
    }
    const preguntasToSave = filterNonBlankTestPreguntas(preguntas);
    const preguntasInvalidMsg = validateTestPreguntasForSave(preguntasToSave, { allowDraft: true });
    if (preguntasInvalidMsg) {
      setMensaje(preguntasInvalidMsg);
      return;
    }
    const completo = { ...testActual, preguntas: preguntasToSave };
    if (modoEdicion) {
      const copia = [...tests];
      copia[indexEdicion] = completo;
      setTests(copia);
      setModoEdicion(false);
      setIndexEdicion(undefined);
      setMensaje("✅ Test modificado (pendiente de guardar en el servidor).");
    } else {
      setTests([...tests, completo]);
      setMensaje("✅ Test añadido (pendiente de guardar en el servidor).");
    }
    resetTestEditor();
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <h1>Tests del curso</h1>
        <button
          className="cc-sidebar-button cc-tests-tab-primary-btn"
          style={{ width: "auto", padding: "8px 16px", border: "none", borderRadius: "4px", cursor: "pointer" }}
          onClick={() => navigate(`/admin/curso/${courseId}/tests-config`)}
        >
          ⚙️ Configuración avanzada
        </button>
      </div>
      {uploadBusy && (
        <p className="cc-message" style={{ marginTop: 6, fontSize: 12 }}>
          Subida en curso…
        </p>
      )}
      <p className="cc-test-editor-muted">
        {getMessage("courseEditor.testsTemario.testsTabHint")}
      </p>
      {pestanasCurso.length === 0 && (
        <div style={{ padding: 16, backgroundColor: "rgba(23,163,152,0.15)", borderRadius: 8, marginBottom: 16, border: "1px solid #17a398" }}>
          <strong>No hay bloques.</strong> Ve a <strong>Datos principales</strong> y añade al menos una pestaña (bloque) antes de crear tests.
        </div>
      )}
      {pestanasCurso.length > 0 && namedPestanas.length === 0 && (
        <div style={{ padding: 16, backgroundColor: "rgba(255,193,7,0.12)", borderRadius: 8, marginBottom: 16, border: "1px solid #ffc107" }}>
          {getMessage("courseEditor.unnamedTabsAllEmptyHint")}
        </div>
      )}
      {unnamedTabsPresent && namedPestanas.length > 0 && (
        <p className="cc-test-editor-warning">
          {getMessage("courseEditor.unnamedTabsSelectHint")}
        </p>
      )}
      <div ref={testEditorRef} id="editar-test-editor">
      <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap", marginBottom: 16 }}>
        <label htmlFor="editar-test-bloque-pestaña" style={{ minWidth: 100 }}>
          Bloque (pestaña):
        </label>
        <ViewportSelect
          id="editar-test-bloque-pestaña"
          value={testActual.pestanas?.[0] || ""}
          onChange={(e) => setTestActual({ ...testActual, pestanas: e.target.value ? [e.target.value] : [] })}
          disabled={editorDisabled || namedPestanas.length === 0}
          title={blockSelectTitle}
          style={{ minWidth: 180 }}
        >
          <option value="">-- Selecciona bloque --</option>
          {namedPestanas.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </ViewportSelect>
      </div>

      <div className="form-group">
        <label htmlFor="editar-test-titulo">Título:</label>
        <input
          id="editar-test-titulo"
          className="form-control medio-ancho"
          value={testActual.titulo}
          onChange={(e) => setTestActual({ ...testActual, titulo: e.target.value })}
          disabled={editorDisabled}
          title={testsLocked ? TITLE_TESTS : undefined}
        />
      </div>
      <div className="form-group">
        <label htmlFor="editar-test-tiempo">Tiempo (min):</label>
        <input
          id="editar-test-tiempo"
          type="number"
          className="form-control medio-ancho"
          value={testActual.tiempo}
          onChange={(e) => setTestActual({ ...testActual, tiempo: Number.parseInt(e.target.value, 10) || 0 })}
          disabled={editorDisabled}
          title={testsLocked ? TITLE_TESTS : undefined}
        />
      </div>
      <div className="form-group">
        <label htmlFor="editar-test-preguntas-por-intento">Preguntas por intento (banco):</label>
        <input
          id="editar-test-preguntas-por-intento"
          type="number"
          min={1}
          className="form-control medio-ancho"
          value={testActual.preguntasPorIntento ?? ""}
          placeholder="Todas si está vacío"
          onChange={(e) => {
            const raw = e.target.value.trim();
            const parsed = raw === "" ? null : Number.parseInt(raw, 10);
            setTestActual({
              ...testActual,
              preguntasPorIntento: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
            });
          }}
          disabled={editorDisabled}
          title={testsLocked ? TITLE_TESTS : undefined}
        />
      </div>

      <div className="crear-test-container">
        <div className="crear-test-form" ref={questionEditorRef} id="editar-test-question-editor">
          <div className="form-group">
            <label htmlFor="editar-test-pregunta-texto">Pregunta:</label>
            <textarea
              id="editar-test-pregunta-texto"
              className="form-control cc-test-pregunta-input"
              value={preguntaActual?.pregunta ?? ""}
              onChange={(e) => {
                const nuevas = [...preguntas];
                if (nuevas[preguntaSeleccionada]) {
                  nuevas[preguntaSeleccionada].pregunta = e.target.value;
                  setPreguntas(nuevas);
                }
              }}
              disabled={editorDisabled}
              title={testsLocked ? TITLE_TESTS : undefined}
              rows={3}
              placeholder="Escribe la pregunta aquí (opcional si hay imagen)"
            />
          </div>
          <div className="form-group">
            <label htmlFor="editar-test-pregunta-imagen">Imagen de la pregunta (opcional):</label>
            {preguntaActual?.imagenUrl ? (
              <img
                src={preguntaActual.imagenUrl}
                alt="Imagen de la pregunta"
                className="imagen-preview"
                style={{ maxWidth: 280, display: "block", marginBottom: 8 }}
              />
            ) : null}
            <input
              id="editar-test-pregunta-imagen"
              type="file"
              accept="image/*"
              className="cc-test-file-input"
              disabled={editorDisabled || uploadBusy}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const result = await uploadTestQuestionImage(file);
                if (!result.ok) {
                  setMensaje(`⚠️ ${result.error}`);
                  return;
                }
                const nuevas = [...preguntas];
                if (nuevas[preguntaSeleccionada]) {
                  nuevas[preguntaSeleccionada] = {
                    ...nuevas[preguntaSeleccionada],
                    imagenUrl: result.url,
                  };
                  setPreguntas(nuevas);
                }
                setMensaje("Imagen de pregunta subida.");
              }}
            />
          </div>

          {(preguntaActual?.respuestas || []).map((respuesta, idx) => (
            <div key={`resp-edit-${preguntaSeleccionada}-${idx}`} className="form-group2">
              <label>
                <input
                  type="radio"
                  name="respuestaCorrecta"
                  checked={preguntaActual?.correcta === idx}
                  disabled={editorDisabled}
                  onChange={() => {
                    const nuevas = [...preguntas];
                    if (nuevas[preguntaSeleccionada]) {
                      nuevas[preguntaSeleccionada].correcta = idx;
                      setPreguntas(nuevas);
                    }
                  }}
                />{" "}
                Respuesta {idx + 1}
              </label>
              <textarea
                className="form-control cc-test-respuesta-input"
                value={respuesta}
                onChange={(e) => {
                  const nuevas = [...preguntas];
                  if (nuevas[preguntaSeleccionada]?.respuestas) {
                    nuevas[preguntaSeleccionada].respuestas[idx] = e.target.value;
                    setPreguntas(nuevas);
                  }
                }}
                disabled={editorDisabled}
                title={testsLocked ? TITLE_TESTS : undefined}
                rows={2}
                placeholder={`Respuesta ${idx + 1}`}
              />
            </div>
          ))}

          <div className="form-group2">
            <label htmlFor="editar-test-explicacion">Explicación de la respuesta correcta:</label>
            <textarea
              id="editar-test-explicacion"
              className="form-control cc-test-explicacion-input"
              value={preguntaActual?.explicacion ?? ""}
              onChange={(e) => {
                const nuevas = [...preguntas];
                if (nuevas[preguntaSeleccionada]) {
                  nuevas[preguntaSeleccionada].explicacion = e.target.value;
                  setPreguntas(nuevas);
                }
              }}
              disabled={editorDisabled}
              title={testsLocked ? TITLE_TESTS : undefined}
              rows={5}
              placeholder="Escribe la explicación aquí..."
            />
          </div>
        </div>

        <div className="crear-test-sidebar">
          <div className="crear-test-sidebar-actions">
            <button
              type="button"
              className="crear-test-sidebar-button"
              disabled={editorDisabled}
              title={testsLocked ? TITLE_TESTS : "Añadir pregunta"}
              onClick={() => {
                setPreguntas([
                  ...preguntas,
                  {
                    pregunta: "",
                    respuestas: ["", "", "", ""],
                    correcta: null,
                    explicacion: "",
                  },
                ]);
                setPreguntaSeleccionada(preguntas.length);
              }}
            >
              ➕
            </button>
            <button
              type="button"
              className="crear-test-sidebar-button crear-test-sidebar-button--dup"
              disabled={testsLocked || uploadBusy || preguntas.length === 0}
              title="Duplicar la pregunta seleccionada"
              onClick={() => {
                const actual = preguntas[preguntaSeleccionada];
                if (!actual) return;
                const copia = {
                  pregunta: actual.pregunta,
                  respuestas: [...(actual.respuestas || ["", "", "", ""])],
                  correcta: actual.correcta,
                  explicacion: actual.explicacion || "",
                  imagenUrl: actual.imagenUrl || "",
                };
                const insertAt = preguntaSeleccionada + 1;
                const nuevas = [...preguntas.slice(0, insertAt), copia, ...preguntas.slice(insertAt)];
                setPreguntas(nuevas);
                setPreguntaSeleccionada(insertAt);
              }}
            >
              ⧉
            </button>
            <button
              type="button"
              className="crear-test-sidebar-button"
              disabled={testsLocked || uploadBusy || preguntas.length <= 1}
              title={testsLocked ? TITLE_TESTS : "Eliminar pregunta seleccionada"}
              onClick={() => {
                if (preguntas.length <= 1) return;
                if (!confirmDeleteTestQuestion(preguntaSeleccionada)) return;
                const nuevas = preguntas.filter((_, i) => i !== preguntaSeleccionada);
                setPreguntas(nuevas);
                setPreguntaSeleccionada((prev) => Math.max(0, prev - 1));
              }}
            >
              ➖
            </button>
          </div>

          <div className="pregunta-lista">
            {preguntas.map((row, idx) => (
              <button
                type="button"
                key={`pregunta-sel-${row.pregunta?.slice(0, 24) ?? "empty"}-${(row.respuestas ?? []).join("").length}-${idx}`}
                onClick={() => {
                  if (testsLocked) return;
                  setPreguntaSeleccionada(idx);
                  scrollToQuestionEditor();
                }}
                title={testsLocked ? TITLE_TESTS : formatTestQuestionSidebarTitle(row, idx)}
                className={`cc-pregunta-item cc-pregunta-item-btn ${idx === preguntaSeleccionada ? "is-active" : ""}`}
                disabled={!canManageTests}
              >
                <span className="cc-pregunta-item__index">Pregunta {idx + 1}</span>
                <span className="cc-pregunta-item__preview">
                  {formatTestQuestionSidebarPreview(row)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      </div>

      <div style={{ marginTop: 24, marginBottom: 24, padding: 16, backgroundColor: "rgba(23,163,152,0.1)", borderRadius: 8, border: "1px solid #17a398" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          <button
            type="button"
            className="style2-button"
            onClick={() => setPreviewTest({ ...testActual, preguntas })}
            disabled={editorDisabled}
            title={testsLocked ? TITLE_TESTS : "Previsualizar el test actual"}
          >
            Vista previa del test
          </button>
          {modoEdicion && (
            <button
              type="button"
              className="style2-button"
              onClick={cancelTestEdition}
              disabled={editorDisabled}
              title={testsLocked ? TITLE_TESTS : "Cancelar la edición del test"}
            >
              Cancelar edición
            </button>
          )}
        </div>
        <button
          className="cc-tests-tab-primary-btn"
          disabled={editorDisabled || primaryBtnNoBlockOrLocked}
          title={saveTestTitle}
          onClick={appendTestToCourse}
          style={{
            width: "100%",
            maxWidth: 400,
            padding: "14px 24px",
            fontSize: 16,
            fontWeight: "bold",
            border: "none",
            borderRadius: 6,
            cursor: primaryBtnNoBlockOrLocked ? "not-allowed" : "pointer",
            transition: "background-color 0.2s",
          }}
        >
          {modoEdicion ? "💾 Guardar cambios del test" : "➕ Añadir test al curso"}
        </button>
        <p style={{ marginTop: 8, fontSize: 12, opacity: 0.9 }}>
          Después pulsa <strong>Guardar cambios</strong> en el menú lateral para enviar al servidor.
        </p>
      </div>

      <h3 style={{ marginTop: 20, marginBottom: 12 }}>Tests por bloque</h3>
      {testsPorBloque.bloquesOrdenados.length === 0 && (tests || []).length === 0 ? (
        <p className="cc-test-editor-muted">No hay tests. Añade uno arriba.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {testsPorBloque.bloquesOrdenados.map((bloque) => {
            const items = testsPorBloque.porBloque[bloque] || [];
            if (items.length === 0) return null;
            return (
              <div key={bloque} style={{ border: "1px solid #17a398", borderRadius: 8, overflow: "hidden", backgroundColor: "rgba(13,27,42,0.6)" }}>
                <div style={{ padding: "10px 16px", backgroundColor: "rgba(23,163,152,0.2)", fontWeight: "bold", fontSize: 15 }}>📁 {bloque}</div>
                <ul style={{ listStyle: "none", padding: "12px 16px", margin: 0 }}>
                  {items.map(({ test: t, index: i }, groupIndex) => {
                    const previousGroupItem = items[groupIndex - 1];
                    const nextGroupItem = items[groupIndex + 1];
                    return (
                    <li
                      key={`${bloque}-${t.titulo ?? "test"}-${i}`}
                      style={{ display: "flex", flexDirection: "column", gap: 10, padding: "12px 0", borderBottom: "1px solid rgba(23,163,152,0.2)" }}
                    >
                      <div className="cc-course-test-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <span className="cc-course-test-summary">
                          <strong>{t.titulo}</strong> — {t.preguntas?.length || 0} preg. — ⏱ {t.tiempo || 0} min
                        </span>
                        <div className="cc-course-test-actions" style={{ display: "flex", gap: 8 }}>
                          <button
                            type="button"
                            aria-label={`Subir test ${t.titulo || "sin título"}`}
                            onClick={() => moveTestToIndex(i, previousGroupItem.index)}
                            disabled={testsLocked || uploadBusy || !previousGroupItem}
                            style={{ padding: "6px 12px", borderRadius: 4, border: "1px solid #17a398", backgroundColor: "transparent", color: "#17a398", cursor: "pointer" }}
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            aria-label={`Bajar test ${t.titulo || "sin título"}`}
                            onClick={() => moveTestToIndex(i, nextGroupItem.index)}
                            disabled={testsLocked || uploadBusy || !nextGroupItem}
                            style={{ padding: "6px 12px", borderRadius: 4, border: "1px solid #17a398", backgroundColor: "transparent", color: "#17a398", cursor: "pointer" }}
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            aria-label={`Vista previa del test ${t.titulo || "sin título"}`}
                            onClick={() => setPreviewTest(t)}
                            disabled={testsLocked || uploadBusy}
                            style={{ padding: "6px 12px", borderRadius: 4, border: "1px solid #17a398", backgroundColor: "transparent", color: "#17a398", cursor: "pointer" }}
                          >
                            Vista previa
                          </button>
                          <button
                            type="button"
                            aria-label={`Editar test ${t.titulo || "sin título"}`}
                            onClick={() => {
                              const draft = cloneTestForEditor(t);
                              setTestActual(draft);
                              setPreguntas(draft.preguntas);
                              setPreguntaSeleccionada(0);
                              setModoEdicion(true);
                              setIndexEdicion(i);
                            }}
                            disabled={testsLocked || uploadBusy}
                            style={{ padding: "6px 12px", borderRadius: 4, border: "1px solid #17a398", backgroundColor: "transparent", color: "#17a398", cursor: "pointer" }}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            aria-label={`Eliminar test ${t.titulo || "sin título"}`}
                            onClick={() => {
                              setDeleteTestIndex(i);
                            }}
                            disabled={testsLocked || uploadBusy}
                            style={{ padding: "6px 12px", borderRadius: 4, border: "1px solid #c62828", backgroundColor: "transparent", color: "#ef5350", cursor: "pointer" }}
                          >
                            Eliminar
                          </button>
                        </div>
                      </div>
                      <CourseTestTemarioLinker
                        test={t}
                        bloquesContenido={bloquesContenido}
                        setBloquesContenido={setBloquesContenido}
                        setMensaje={setMensaje}
                        disabled={testsLocked || uploadBusy}
                      />
                    </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      <PreviewModalSlot previewTest={previewTest} onClose={() => setPreviewTest(null)} />
      <DeleteTestConfirmModal
        open={deleteTestIndex !== undefined}
        onConfirm={confirmDeleteSelectedTest}
        onCancel={() => setDeleteTestIndex(undefined)}
      />
      <p className="cc-test-editor-hint">💾 Pulsa “Guardar cambios” en el menú lateral para enviar al servidor.</p>
    </>
  );
}

EditarCursoTestsTab.propTypes = {
  courseId: PropTypes.string.isRequired,
  uploadStatus: PropTypes.string.isRequired,
  pestanasCurso: PropTypes.arrayOf(PropTypes.string).isRequired,
  canManageTests: PropTypes.bool.isRequired,
  testActual: testShape.isRequired,
  setTestActual: PropTypes.func.isRequired,
  preguntas: PropTypes.arrayOf(preguntaShape).isRequired,
  setPreguntas: PropTypes.func.isRequired,
  preguntaSeleccionada: PropTypes.number.isRequired,
  setPreguntaSeleccionada: PropTypes.func.isRequired,
  preguntaActual: preguntaShape,
  modoEdicion: PropTypes.bool.isRequired,
  setModoEdicion: PropTypes.func.isRequired,
  indexEdicion: PropTypes.number,
  setIndexEdicion: PropTypes.func.isRequired,
  tests: PropTypes.arrayOf(testShape).isRequired,
  setTests: PropTypes.func.isRequired,
  setMensaje: PropTypes.func.isRequired,
  testsPorBloque: testsPorBloqueShape.isRequired,
  bloquesContenido: PropTypes.objectOf(PropTypes.array).isRequired,
  setBloquesContenido: PropTypes.func.isRequired,
};
